/**
 * useCreateGroupState Hook
 * Manages all state for CreateGroup component
 * Replaces 65+ state variables scattered throughout CreateGroup.tsx
 */

import { useState, useCallback } from 'react';
import type {
  GroupDetails,
  GroupType
} from '@/services/api/groups/groupsGraphqlService';
import type {
  FilterOption
} from '@/services/features/GroupFilteringService';
import type {
  ConsolidatedPatientData as Patient,
  Clinic,
  Facility,
  ClinicProvider as Provider
} from '@/services/api';

export interface CreateGroupState {
  // Form state
  createType: 'filters' | 'emrs' | '';
  groupName: string;
  selectedVPEs: number[];
  vpeManuallyInteracted: boolean;
  selectedGroupType: string;
  selectedClinic: string;
  selectedFacilities: number[];
  selectedProviders: number[];
  
  // Filter states
  selectedPrimaryDx: string[];
  selectedSecondaryDx: string[];
  selectedProgramEligibility: string[];
  selectedPrimaryInsurance: string[];
  selectedSecondaryInsurance: string[];
  selectedInsuranceType: string[];
  zipCodes: string;
  lastVisitDate: string;
  excludeAssignedGroups: boolean;

  // Patient search and display
  patientSearchTerm: string;
  searchResults: string[];
  selectedPatients: number[];
  selectedPatientsForGroup: number[];
  numberOfPatientsToShow: string;
  selectedPatientForAdding: string;
  showPatientList: boolean;
  patients: Patient[];
  filteredPatientsCount: number;
  currentPage: number;
  totalPages: number;
  selectAll: boolean;
  patientsPerPage: number;

  // Search states
  clinicSearchTerm: string;
  showClinicDropdown: boolean;

  // EMRs states
  patientEmrs: string;
  showEmrPatientList: boolean;
  emrPatients: Patient[];
  selectedEmrPatients: number[];
  emrSelectAll: boolean;

  // Clinic slots states
  startTime: string;
  endTime: string;
  slotDuration: string;
  availableDays: string[];
  maxAppointmentsPerSlot: string;
  timeZone: string;

  // Data state
  groupTypes: GroupType[];
  clinics: Clinic[];
  facilities: Facility[];
  providers: Provider[];
  
  // Filter options
  primaryDxOptions: FilterOption[];
  secondaryDxOptions: FilterOption[];
  primaryInsuranceOptions: FilterOption[];
  secondaryInsuranceOptions: FilterOption[];
  insuranceTypeOptions: FilterOption[];
  
  // UI state
  isLoading: boolean;
  showFilters: boolean;
  isDuplicateMode: boolean;
  isEditMode: boolean;
  isUserEdited: boolean;
  originalPatientCount: number;
  groupDetails: GroupDetails | null;
  originalValues: any;
}

export interface CreateGroupStateActions {
  // Form actions
  setCreateType: (type: 'filters' | 'emrs' | '') => void;
  setGroupName: (name: string) => void;
  setSelectedVPEs: (vpes: number[]) => void;
  setVpeManuallyInteracted: (interacted: boolean) => void;
  setSelectedGroupType: (type: string) => void;
  setSelectedClinic: (clinic: string) => void;
  setSelectedFacilities: (facilities: number[]) => void;
  setSelectedProviders: (providers: number[]) => void;

  // Filter actions
  setSelectedPrimaryDx: (dx: string[]) => void;
  setSelectedSecondaryDx: (dx: string[]) => void;
  setSelectedProgramEligibility: (eligibility: string[]) => void;
  setSelectedPrimaryInsurance: (insurance: string[]) => void;
  setSelectedSecondaryInsurance: (insurance: string[]) => void;
  setSelectedInsuranceType: (type: string[]) => void;
  setZipCodes: (codes: string) => void;
  setLastVisitDate: (date: string) => void;
  setExcludeAssignedGroups: (exclude: boolean) => void;

  // Patient actions
  setPatientSearchTerm: (term: string) => void;
  setSearchResults: (results: string[]) => void;
  setSelectedPatients: (patients: number[]) => void;
  setSelectedPatientsForGroup: (patients: number[]) => void;
  setNumberOfPatientsToShow: (number: string) => void;
  setSelectedPatientForAdding: (patient: string) => void;
  setShowPatientList: (show: boolean) => void;
  setPatients: (patients: Patient[]) => void;
  setFilteredPatientsCount: (count: number) => void;
  setCurrentPage: (page: number) => void;
  setTotalPages: (pages: number) => void;
  setSelectAll: (selectAll: boolean) => void;
  setPatientsPerPage: (perPage: number) => void;

  // Search actions
  setClinicSearchTerm: (term: string) => void;
  setShowClinicDropdown: (show: boolean) => void;

  // EMR actions
  setPatientEmrs: (emrs: string) => void;
  setShowEmrPatientList: (show: boolean) => void;
  setEmrPatients: (patients: Patient[]) => void;
  setSelectedEmrPatients: (patients: number[]) => void;
  setEmrSelectAll: (selectAll: boolean) => void;

  // Clinic slots actions
  setStartTime: (time: string) => void;
  setEndTime: (time: string) => void;
  setSlotDuration: (duration: string) => void;
  setAvailableDays: (days: string[]) => void;
  setMaxAppointmentsPerSlot: (max: string) => void;
  setTimeZone: (zone: string) => void;

  // Data actions
  setGroupTypes: (types: GroupType[]) => void;
  setClinics: (clinics: Clinic[]) => void;
  setFacilities: (facilities: Facility[]) => void;
  setProviders: (providers: Provider[]) => void;
  setPrimaryDxOptions: (options: FilterOption[]) => void;
  setSecondaryDxOptions: (options: FilterOption[]) => void;
  setPrimaryInsuranceOptions: (options: FilterOption[]) => void;
  setSecondaryInsuranceOptions: (options: FilterOption[]) => void;
  setInsuranceTypeOptions: (options: FilterOption[]) => void;

  // UI actions
  setIsLoading: (loading: boolean) => void;
  setShowFilters: (show: boolean) => void;
  setIsDuplicateMode: (duplicate: boolean) => void;
  setIsEditMode: (edit: boolean) => void;
  setIsUserEdited: (edited: boolean) => void;
  setOriginalPatientCount: (count: number) => void;
  setGroupDetails: (details: GroupDetails | null) => void;
  setOriginalValues: (values: any) => void;

  // Utility actions
  resetFilters: () => void;
  resetForm: () => void;
}

const initialState: CreateGroupState = {
  // Form state
  createType: '',
  groupName: '',
  selectedVPEs: [],
  vpeManuallyInteracted: false,
  selectedGroupType: '',
  selectedClinic: '',
  selectedFacilities: [],
  selectedProviders: [],
  
  // Filter states
  selectedPrimaryDx: [],
  selectedSecondaryDx: [],
  selectedProgramEligibility: [],
  selectedPrimaryInsurance: [],
  selectedSecondaryInsurance: [],
  selectedInsuranceType: [],
  zipCodes: '',
  lastVisitDate: '',
  excludeAssignedGroups: false,

  // Patient search and display
  patientSearchTerm: '',
  searchResults: [],
  selectedPatients: [],
  selectedPatientsForGroup: [],
  numberOfPatientsToShow: '',
  selectedPatientForAdding: '',
  showPatientList: false,
  patients: [],
  filteredPatientsCount: 0,
  currentPage: 1,
  totalPages: 1,
  selectAll: false,
  patientsPerPage: 10,

  // Search states
  clinicSearchTerm: '',
  showClinicDropdown: false,

  // EMRs states
  patientEmrs: '',
  showEmrPatientList: false,
  emrPatients: [],
  selectedEmrPatients: [],
  emrSelectAll: false,

  // Clinic slots states
  startTime: '',
  endTime: '',
  slotDuration: '',
  availableDays: [],
  maxAppointmentsPerSlot: '',
  timeZone: '',

  // Data state
  groupTypes: [],
  clinics: [],
  facilities: [],
  providers: [],
  
  // Filter options
  primaryDxOptions: [],
  secondaryDxOptions: [],
  primaryInsuranceOptions: [],
  secondaryInsuranceOptions: [],
  insuranceTypeOptions: [],
  
  // UI state
  isLoading: false,
  showFilters: false,
  isDuplicateMode: false,
  isEditMode: false,
  isUserEdited: false,
  originalPatientCount: 0,
  groupDetails: null,
  originalValues: null,
};

export const useCreateGroupState = () => {
  const [state, setState] = useState<CreateGroupState>(initialState);

  // Create action functions using useCallback for performance
  const actions: CreateGroupStateActions = {
    // Form actions
    setCreateType: useCallback((createType: 'filters' | 'emrs' | '') => {
      setState(prev => ({ ...prev, createType }));
    }, []),

    setGroupName: useCallback((groupName: string) => {
      setState(prev => ({ ...prev, groupName }));
    }, []),

    setSelectedVPEs: useCallback((selectedVPEs: number[]) => {
      setState(prev => ({ ...prev, selectedVPEs }));
    }, []),

    setVpeManuallyInteracted: useCallback((vpeManuallyInteracted: boolean) => {
      setState(prev => ({ ...prev, vpeManuallyInteracted }));
    }, []),

    setSelectedGroupType: useCallback((selectedGroupType: string) => {
      setState(prev => ({ ...prev, selectedGroupType }));
    }, []),

    setSelectedClinic: useCallback((selectedClinic: string) => {
      setState(prev => ({ ...prev, selectedClinic }));
    }, []),

    setSelectedFacilities: useCallback((selectedFacilities: number[]) => {
      setState(prev => ({ ...prev, selectedFacilities }));
    }, []),

    setSelectedProviders: useCallback((selectedProviders: number[]) => {
      setState(prev => ({ ...prev, selectedProviders }));
    }, []),

    // Filter actions
    setSelectedPrimaryDx: useCallback((selectedPrimaryDx: string[]) => {
      setState(prev => ({ ...prev, selectedPrimaryDx }));
    }, []),

    setSelectedSecondaryDx: useCallback((selectedSecondaryDx: string[]) => {
      setState(prev => ({ ...prev, selectedSecondaryDx }));
    }, []),

    setSelectedProgramEligibility: useCallback((selectedProgramEligibility: string[]) => {
      setState(prev => ({ ...prev, selectedProgramEligibility }));
    }, []),

    setSelectedPrimaryInsurance: useCallback((selectedPrimaryInsurance: string[]) => {
      setState(prev => ({ ...prev, selectedPrimaryInsurance }));
    }, []),

    setSelectedSecondaryInsurance: useCallback((selectedSecondaryInsurance: string[]) => {
      setState(prev => ({ ...prev, selectedSecondaryInsurance }));
    }, []),

    setSelectedInsuranceType: useCallback((selectedInsuranceType: string[]) => {
      setState(prev => ({ ...prev, selectedInsuranceType }));
    }, []),

    setZipCodes: useCallback((zipCodes: string) => {
      setState(prev => ({ ...prev, zipCodes }));
    }, []),

    setLastVisitDate: useCallback((lastVisitDate: string) => {
      setState(prev => ({ ...prev, lastVisitDate }));
    }, []),

    setExcludeAssignedGroups: useCallback((excludeAssignedGroups: boolean) => {
      setState(prev => ({ ...prev, excludeAssignedGroups }));
    }, []),

    // Patient actions
    setPatientSearchTerm: useCallback((patientSearchTerm: string) => {
      setState(prev => ({ ...prev, patientSearchTerm }));
    }, []),

    setSearchResults: useCallback((searchResults: string[]) => {
      setState(prev => ({ ...prev, searchResults }));
    }, []),

    setSelectedPatients: useCallback((selectedPatients: number[]) => {
      setState(prev => ({ ...prev, selectedPatients }));
    }, []),

    setSelectedPatientsForGroup: useCallback((selectedPatientsForGroup: number[]) => {
      setState(prev => ({ ...prev, selectedPatientsForGroup }));
    }, []),

    setNumberOfPatientsToShow: useCallback((numberOfPatientsToShow: string) => {
      setState(prev => ({ ...prev, numberOfPatientsToShow }));
    }, []),

    setSelectedPatientForAdding: useCallback((selectedPatientForAdding: string) => {
      setState(prev => ({ ...prev, selectedPatientForAdding }));
    }, []),

    setShowPatientList: useCallback((showPatientList: boolean) => {
      setState(prev => ({ ...prev, showPatientList }));
    }, []),

    setPatients: useCallback((patients: Patient[]) => {
      setState(prev => ({ ...prev, patients }));
    }, []),

    setFilteredPatientsCount: useCallback((filteredPatientsCount: number) => {
      setState(prev => ({ ...prev, filteredPatientsCount }));
    }, []),

    setCurrentPage: useCallback((currentPage: number) => {
      setState(prev => ({ ...prev, currentPage }));
    }, []),

    setTotalPages: useCallback((totalPages: number) => {
      setState(prev => ({ ...prev, totalPages }));
    }, []),

    setSelectAll: useCallback((selectAll: boolean) => {
      setState(prev => ({ ...prev, selectAll }));
    }, []),

    setPatientsPerPage: useCallback((patientsPerPage: number) => {
      setState(prev => ({ ...prev, patientsPerPage }));
    }, []),

    // Search actions
    setClinicSearchTerm: useCallback((clinicSearchTerm: string) => {
      setState(prev => ({ ...prev, clinicSearchTerm }));
    }, []),

    setShowClinicDropdown: useCallback((showClinicDropdown: boolean) => {
      setState(prev => ({ ...prev, showClinicDropdown }));
    }, []),

    // EMR actions
    setPatientEmrs: useCallback((patientEmrs: string) => {
      setState(prev => ({ ...prev, patientEmrs }));
    }, []),

    setShowEmrPatientList: useCallback((showEmrPatientList: boolean) => {
      setState(prev => ({ ...prev, showEmrPatientList }));
    }, []),

    setEmrPatients: useCallback((emrPatients: Patient[]) => {
      setState(prev => ({ ...prev, emrPatients }));
    }, []),

    setSelectedEmrPatients: useCallback((selectedEmrPatients: number[]) => {
      setState(prev => ({ ...prev, selectedEmrPatients }));
    }, []),

    setEmrSelectAll: useCallback((emrSelectAll: boolean) => {
      setState(prev => ({ ...prev, emrSelectAll }));
    }, []),

    // Clinic slots actions
    setStartTime: useCallback((startTime: string) => {
      setState(prev => ({ ...prev, startTime }));
    }, []),

    setEndTime: useCallback((endTime: string) => {
      setState(prev => ({ ...prev, endTime }));
    }, []),

    setSlotDuration: useCallback((slotDuration: string) => {
      setState(prev => ({ ...prev, slotDuration }));
    }, []),

    setAvailableDays: useCallback((availableDays: string[]) => {
      setState(prev => ({ ...prev, availableDays }));
    }, []),

    setMaxAppointmentsPerSlot: useCallback((maxAppointmentsPerSlot: string) => {
      setState(prev => ({ ...prev, maxAppointmentsPerSlot }));
    }, []),

    setTimeZone: useCallback((timeZone: string) => {
      setState(prev => ({ ...prev, timeZone }));
    }, []),

    // Data actions
    setGroupTypes: useCallback((groupTypes: GroupType[]) => {
      setState(prev => ({ ...prev, groupTypes }));
    }, []),

    setClinics: useCallback((clinics: Clinic[]) => {
      setState(prev => ({ ...prev, clinics }));
    }, []),

    setFacilities: useCallback((facilities: Facility[]) => {
      setState(prev => ({ ...prev, facilities }));
    }, []),

    setProviders: useCallback((providers: Provider[]) => {
      setState(prev => ({ ...prev, providers }));
    }, []),

    setPrimaryDxOptions: useCallback((primaryDxOptions: FilterOption[]) => {
      setState(prev => ({ ...prev, primaryDxOptions }));
    }, []),

    setSecondaryDxOptions: useCallback((secondaryDxOptions: FilterOption[]) => {
      setState(prev => ({ ...prev, secondaryDxOptions }));
    }, []),

    setPrimaryInsuranceOptions: useCallback((primaryInsuranceOptions: FilterOption[]) => {
      setState(prev => ({ ...prev, primaryInsuranceOptions }));
    }, []),

    setSecondaryInsuranceOptions: useCallback((secondaryInsuranceOptions: FilterOption[]) => {
      setState(prev => ({ ...prev, secondaryInsuranceOptions }));
    }, []),

    setInsuranceTypeOptions: useCallback((insuranceTypeOptions: FilterOption[]) => {
      setState(prev => ({ ...prev, insuranceTypeOptions }));
    }, []),

    // UI actions
    setIsLoading: useCallback((isLoading: boolean) => {
      setState(prev => ({ ...prev, isLoading }));
    }, []),

    setShowFilters: useCallback((showFilters: boolean) => {
      setState(prev => ({ ...prev, showFilters }));
    }, []),

    setIsDuplicateMode: useCallback((isDuplicateMode: boolean) => {
      setState(prev => ({ ...prev, isDuplicateMode }));
    }, []),

    setIsEditMode: useCallback((isEditMode: boolean) => {
      setState(prev => ({ ...prev, isEditMode }));
    }, []),

    setIsUserEdited: useCallback((isUserEdited: boolean) => {
      setState(prev => ({ ...prev, isUserEdited }));
    }, []),

    setOriginalPatientCount: useCallback((originalPatientCount: number) => {
      setState(prev => ({ ...prev, originalPatientCount }));
    }, []),

    setGroupDetails: useCallback((groupDetails: GroupDetails | null) => {
      setState(prev => ({ ...prev, groupDetails }));
    }, []),

    setOriginalValues: useCallback((originalValues: any) => {
      setState(prev => ({ ...prev, originalValues }));
    }, []),

    // Utility actions
    resetFilters: useCallback(() => {
      setState(prev => ({
        ...prev,
        selectedPrimaryDx: [],
        selectedSecondaryDx: [],
        selectedProgramEligibility: [],
        selectedPrimaryInsurance: [],
        selectedSecondaryInsurance: [],
        selectedInsuranceType: [],
        primaryDxOptions: [],
        secondaryDxOptions: [],
        primaryInsuranceOptions: [],
        secondaryInsuranceOptions: [],
        insuranceTypeOptions: [],
        filteredPatientsCount: 0,
        selectedPatients: [],
      }));
    }, []),

    resetForm: useCallback(() => {
      setState(initialState);
    }, []),
  };

  return { state, actions };
};